import { useEffect } from "react";

/* Injects GTM / GA4 / custom head + body scripts based on /api/site-scripts.
   Runs once on mount, deferred via requestIdleCallback to avoid blocking initial
   paint. Gated to production hostnames so preview env doesn't load real analytics. */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Production hosts where it's appropriate to load analytics + custom scripts.
// Preview / localhost stays quiet to keep dev console clean and fast.
const PROD_HOSTS = ["clinicfirst.ca", "www.clinicfirst.ca"];
const isProdHost = () =>
  typeof window !== "undefined" && PROD_HOSTS.some((h) => window.location.hostname.endsWith(h));

export const SiteScripts = () => {
  useEffect(() => {
    if (!isProdHost()) return; // skip on preview/dev — no 404s, faster paint

    const run = () => {
      fetch(`${API}/site-scripts`)
        .then((r) => r.ok ? r.json() : null)
        .then((cfg) => {
          if (!cfg) return;

        // GTM
        if (cfg.gtm_id && !document.getElementById("co-gtm")) {
          const s = document.createElement("script");
          s.id = "co-gtm";
          s.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${cfg.gtm_id}');`;
          document.head.appendChild(s);
        }

        // GA4
        if (cfg.ga4_id && !document.getElementById("co-ga4")) {
          const lib = document.createElement("script");
          lib.async = true;
          lib.src = `https://www.googletagmanager.com/gtag/js?id=${cfg.ga4_id}`;
          document.head.appendChild(lib);
          const init = document.createElement("script");
          init.id = "co-ga4";
          init.innerHTML = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${cfg.ga4_id}');`;
          document.head.appendChild(init);
        }

        // Custom head scripts (raw HTML — supports <script> + arbitrary <meta>)
        if (cfg.head_scripts && !document.getElementById("co-head-custom")) {
          const wrap = document.createElement("div");
          wrap.id = "co-head-custom";
          wrap.style.display = "none";
          wrap.innerHTML = cfg.head_scripts;
          // Move children to head (scripts will execute when re-inserted via element.cloneNode)
          Array.from(wrap.childNodes).forEach((n) => {
            if (n.tagName === "SCRIPT") {
              const ns = document.createElement("script");
              for (const a of n.attributes) ns.setAttribute(a.name, a.value);
              ns.text = n.text;
              document.head.appendChild(ns);
            } else {
              document.head.appendChild(n);
            }
          });
        }

        // Body scripts — same trick, append to <body>
        if (cfg.body_scripts && !document.getElementById("co-body-custom")) {
          const wrap = document.createElement("div");
          wrap.id = "co-body-custom";
          wrap.style.display = "none";
          wrap.innerHTML = cfg.body_scripts;
          Array.from(wrap.childNodes).forEach((n) => {
            if (n.tagName === "SCRIPT") {
              const ns = document.createElement("script");
              for (const a of n.attributes) ns.setAttribute(a.name, a.value);
              ns.text = n.text;
              document.body.appendChild(ns);
            } else {
              document.body.appendChild(n);
            }
          });
        }
      })
      .catch(() => {});
    };

    // Defer to next idle slot so it never blocks first paint
    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(run, { timeout: 2000 });
    } else {
      setTimeout(run, 1000);
    }
  }, []);
  return null;
};
